import React, { useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';

interface BadgeData {
  nome: string;
  cognome: string;
  azienda?: string | null;
  courseName: string;
  editionLabel?: string | null;
  token: string;
  badgeBgUrl?: string | null;
}

interface BadgeModalProps {
  data: BadgeData;
  onClose: () => void;
}

export default function BadgeModal({ data, onClose }: BadgeModalProps) {
  const badgeRef = useRef<HTMLDivElement>(null);

  // Resolve absolute URL for print window (which runs in a separate context)
  const absBgUrl = data.badgeBgUrl
    ? `${window.location.origin}${data.badgeBgUrl}`
    : null;

  const handlePrint = () => {
    const printWindow = window.open('', '_blank', 'width=800,height=900');
    if (!printWindow || !badgeRef.current) return;

    const qrSvg = badgeRef.current.querySelector('svg')?.outerHTML ?? '';
    const hasBg = !!absBgUrl;

    printWindow.document.write(`<!DOCTYPE html>
<html lang="it">
<head>
  <meta charset="UTF-8" />
  <title>Badge – ${data.nome} ${data.cognome}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      background: #f0f0f0;
      display: flex; align-items: center; justify-content: center;
      min-height: 100vh;
      font-family: 'Segoe UI', Arial, sans-serif;
    }
    .badge-outer {
      width: 90mm;
      min-height: 130mm;
      position: relative;
      border-radius: 10px;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      ${hasBg
        ? `background: url('${absBgUrl}') center/cover no-repeat; border: none;`
        : `background: #fff; border: 2px solid #0c2a4a;`
      }
    }
    .badge-content {
      ${hasBg
        ? `background: rgba(255,255,255,0.92); border-radius: 8px; margin: 12mm 10mm; padding: 8mm 8mm;`
        : `padding: 12mm 10mm; width: 100%;`
      }
      text-align: center;
      width: calc(100% - 20mm);
    }
    .badge-header {
      background: #0c2a4a; color: #fff;
      border-radius: 4px; padding: 4mm 0; margin-bottom: 6mm;
      font-size: 9pt; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase;
    }
    .badge-qr { display: flex; justify-content: center; margin-bottom: 5mm; }
    .badge-name { font-size: 17pt; font-weight: 800; color: #0c2a4a; line-height: 1.2; margin-bottom: 1.5mm; }
    .badge-azienda { font-size: 10pt; color: #e07b39; font-weight: 600; margin-bottom: 4mm; }
    .badge-divider { border: none; border-top: 1px solid #e2e8f0; margin: 3mm 0; }
    .badge-course { font-size: 8.5pt; color: #475569; line-height: 1.5; }
    .badge-course strong { color: #0c2a4a; font-size: 9.5pt; display: block; margin-bottom: 1mm; }
    @media print {
      @page { size: A6 portrait; margin: 0; }
      body { background: transparent; min-height: auto; }
      .badge-outer { width: 100%; min-height: 100vh; border-radius: 0; border: none; }
    }
  </style>
</head>
<body>
  <div class="badge-outer">
    <div class="badge-content">
      ${!hasBg ? `<div class="badge-header">AdriaClimPlus - Learning</div>` : ''}
      <div class="badge-qr">${qrSvg}</div>
      <div class="badge-name">${data.nome} ${data.cognome}</div>
      ${data.azienda ? `<div class="badge-azienda">${data.azienda}</div>` : ''}
      <div class="badge-divider"></div>
      <div class="badge-course">
        <strong>${data.courseName}</strong>
        ${data.editionLabel ? data.editionLabel : ''}
      </div>
    </div>
  </div>
  <script>window.onload = () => { window.print(); window.onafterprint = () => window.close(); }<\/script>
</body>
</html>`);
    printWindow.document.close();
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem',
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 16, boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
        padding: '2rem', maxWidth: 480, width: '100%',
        display: 'flex', flexDirection: 'column', gap: '1.5rem',
      }} onClick={e => e.stopPropagation()}>

        {/* Header modale */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontFamily: 'var(--font-public)', fontWeight: 800, fontSize: '1.25rem', color: 'var(--pub-navy)', margin: 0 }}>
            Anteprima Badge
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.25rem', color: 'var(--gray-400)', padding: '0.25rem' }}>✕</button>
        </div>

        {/* Badge preview */}
        <div style={{
          position: 'relative', borderRadius: 10, overflow: 'hidden',
          border: absBgUrl ? 'none' : '2px solid var(--pub-navy)',
          ...(absBgUrl
            ? { backgroundImage: `url(${absBgUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
            : {}),
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          minHeight: 320,
        }}>
          <div ref={badgeRef} style={{
            ...(absBgUrl
              ? { background: 'rgba(255,255,255,0.92)', borderRadius: 8, margin: '1.5rem', padding: '1.25rem' }
              : { padding: '1.5rem 1.25rem', width: '100%' }),
            textAlign: 'center',
          }}>
            {/* Header fascia (solo senza sfondo) */}
            {!absBgUrl && (
              <div style={{
                background: 'var(--pub-navy)', color: '#fff', borderRadius: 6,
                padding: '0.5rem 0', marginBottom: '1.25rem',
                fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
                fontFamily: 'var(--font-public)',
              }}>
                AdriaClimPlus - Learning
              </div>
            )}

            {/* QR code */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
              <div style={{ padding: 6, background: '#fff', border: '1px solid var(--gray-100)', borderRadius: 6, display: 'inline-block' }}>
                <QRCodeSVG
                  value={`${window.location.origin}/area?token=${data.token}`}
                  size={100}
                  level="M"
                  includeMargin={false}
                />
              </div>
            </div>

            {/* Nome */}
            <div style={{ fontSize: '1.375rem', fontWeight: 800, color: 'var(--pub-navy)', lineHeight: 1.15, marginBottom: '0.25rem', fontFamily: 'var(--font-public)' }}>
              {data.nome} {data.cognome}
            </div>

            {/* Azienda */}
            {data.azienda && (
              <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--pub-orange)', marginBottom: '0.875rem' }}>
                {data.azienda}
              </div>
            )}

            {/* Divisore */}
            <hr style={{ border: 'none', borderTop: '1px solid var(--gray-100)', margin: '0.625rem 0' }} />

            {/* Corso */}
            <div style={{ fontSize: '0.8125rem', color: 'var(--gray-500)', lineHeight: 1.55 }}>
              <strong style={{ color: 'var(--pub-navy)', display: 'block', marginBottom: '0.2rem' }}>{data.courseName}</strong>
              {data.editionLabel && <span>{data.editionLabel}</span>}
            </div>
          </div>
        </div>

        {/* Azioni */}
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button onClick={onClose} className="btn-pub-outline" style={{ flex: 1 }}>Chiudi</button>
          <button onClick={handlePrint} className="btn-pub-cta" style={{ flex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
            🖨️ Stampa Badge
          </button>
        </div>
      </div>
    </div>
  );
}
